// The side drawer that adding a secret and replacing its value open in (RizzUI Drawer, as in
// design/isomorphic/src/pages/secret-drawer.tsx): a header with back and close, a scrolling
// body, and a footer that says which device signs the change.
import { ArrowLeft, LockKey, X } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { ActionIcon, Drawer } from "rizzui";

import { useT } from "../i18n";

export function SecretDrawer({ open, onClose, children }: { open: boolean; onClose: () => void; children: ReactNode }) {
  return (
    <Drawer isOpen={open} onClose={onClose} customSize={520} containerClassName="max-w-full dark:bg-gray-50">
      <div className="flex h-full flex-col">{children}</div>
    </Drawer>
  );
}

export function DrawerHeader({ title, onBack, onClose }: { title: string; onBack?: () => void; onClose: () => void }) {
  const { actions } = useT().common;
  return (
    <div className="flex items-center justify-between gap-3 border-b border-muted px-5 py-4">
      <div className="flex min-w-0 items-center gap-2">
        {onBack ? (
          <ActionIcon size="sm" variant="text" onClick={onBack} aria-label={actions.back}>
            <ArrowLeft weight="bold" className="h-4 w-4" />
          </ActionIcon>
        ) : null}
        <h2 className="truncate text-lg">{title}</h2>
      </div>
      <ActionIcon size="sm" variant="text" onClick={onClose} aria-label={actions.close}>
        <X weight="bold" className="h-4 w-4" />
      </ActionIcon>
    </div>
  );
}

export function DrawerBody({ children }: { children: ReactNode }) {
  return <div className="custom-scrollbar flex-1 space-y-5 overflow-y-auto p-5">{children}</div>;
}

/** A field with a quiet hint under it (RizzUI's helperText is a live region, too loud for a hint). */
export function Hinted({ hint, children }: { hint: string; children: ReactNode }) {
  return (
    <div>
      {children}
      <p className="mb-0 mt-1 text-[13px] text-gray-500">{hint}</p>
    </div>
  );
}

/** The footer of a signed change: who signs it, then the buttons. */
export function DrawerFooter({ signedBy, children }: { signedBy: string; children: ReactNode }) {
  const t = useT().secrets.forms;
  return (
    <div className="flex items-center gap-3 border-t border-muted px-5 py-4">
      <span className="me-auto flex min-w-0 items-center gap-1.5 text-xs text-gray-500">
        <LockKey weight="fill" className="h-3.5 w-3.5 shrink-0 text-secondary" /> {t.signedBy(signedBy)}
      </span>
      {children}
    </div>
  );
}
